import { useState } from 'react';
import { NotificationsDropdown } from '@/components/app/NotificationsDropdown';
import { OrganizationSwitcher } from '@/components/app/OrganizationSwitcher';
import { useAuth } from '@/providers/AuthProvider';
import { LogOut, Menu, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';

export function AppTopBar({ onMenuToggle }: { onMenuToggle?: () => void }) {
  const { user, logout, organization } = useAuth();
  const navigate = useNavigate();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const firstName = user?.firstName ?? user?.email?.split('@')[0] ?? 'there';
  const lastName = user?.lastName ?? '';
  const initials =
    firstName && lastName
      ? `${firstName[0]}${lastName[0]}`.toUpperCase()
      : firstName
        ? firstName.slice(0, 2).toUpperCase()
        : '?';

  return (
    <header className="sticky z-30 rounded-2xl backdrop-blur-xl"
      style={{
        top: 'max(1rem, calc(0.5rem + env(safe-area-inset-top, 0px)))',
        background: 'var(--ui-topbar)',
        border: '1px solid var(--ui-border)',
        boxShadow: 'var(--ui-topbar-shadow)',
      }}
    >
      {/* Mobile search expansion */}
      {mobileSearchOpen && (
        <div className="flex items-center gap-2 px-4 py-3 sm:hidden">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
            <input
              type="search"
              autoFocus
              placeholder="Search campaigns, audiences…"
              className="w-full rounded-xl py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:outline-none transition-all duration-200" style={{ background: 'var(--ui-card)', border: '1px solid var(--ui-border)' }}
            />
          </div>
          <button
            type="button"
            onClick={() => setMobileSearchOpen(false)}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close search"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main bar */}
      <div className={cn('flex items-center gap-2 px-4 py-3', mobileSearchOpen && 'hidden sm:flex')}>
        {/* Hamburger — mobile only */}
        {onMenuToggle && (
          <button
            type="button"
            onClick={onMenuToggle}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-4 w-4" />
          </button>
        )}

        {/* Search — always visible on sm+, icon-only on xs */}
        <div className="relative hidden min-w-0 flex-1 max-w-sm sm:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
          <input
            type="search"
            placeholder="Search campaigns, audiences…"
            className="w-full rounded-xl py-2 pl-9 pr-12 text-sm text-white placeholder:text-white/40 focus:outline-none transition-all duration-200"
            style={{ background: 'var(--ui-card)', border: '1px solid var(--ui-border)' }}
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-white/[0.08] bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-white/25 hidden sm:inline-flex">
            ⌘K
          </kbd>
        </div>

        {/* Search icon — only on very small screens (< sm) */}
        <button
          type="button"
          onClick={() => setMobileSearchOpen(true)}
          className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 transition-colors hover:bg-white/10 hover:text-white sm:hidden"
          aria-label="Search"
        >
          <Search className="h-4 w-4" />
        </button>

        {/* Spacer on mobile to push right controls flush */}
        <div className="flex-1 sm:hidden" />

        {/* Center: org switcher (md+) */}
        <div className="hidden items-center gap-2 md:flex">
          <OrganizationSwitcher />
        </div>

        {/* Right controls */}
        <div className="flex flex-none items-center gap-1.5 sm:gap-2">
          {/* Notifications */}
          <NotificationsDropdown />

          {/* Divider */}
          <div className="hidden h-6 w-px bg-white/10 sm:block" />

          {/* User avatar + name */}
          <div className="flex items-center gap-2">
            <div
              className="flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-accent-500/30 bg-accent-500/15 text-xs font-bold text-accent-200"
              aria-label={`User: ${firstName}`}
            >
              {initials}
            </div>
            <div className="hidden flex-col sm:flex">
              <span className="text-[13px] font-semibold text-white leading-tight">
                {firstName} {lastName}
              </span>
              {organization ? (
                <span className="text-[11px] text-white/40 leading-tight">{organization.name}</span>
              ) : null}
            </div>
          </div>

          {/* Logout */}
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/');
            }}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/40 transition-colors hover:bg-danger/10 hover:border-danger/30 hover:text-danger"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
